import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FileText, RefreshCw, CheckCircle, Mail, Download, Info, Phone } from 'lucide-react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OvhImport() {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [imapScanning, setImapScanning] = useState(false);
    const [imapResult, setImapResult] = useState(null);
    const [apiSyncing, setApiSyncing] = useState(false);
    const [apiSyncResult, setApiSyncResult] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) setFile(f);
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setResult(null);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await axios.post(`${API_URL}/api/ovh/import-csv`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setResult(r.data);
            toast.success(`${r.data.inserted} appels importés`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur import');
        } finally {
            setUploading(false);
        }
    };

    const handleScanImap = async () => {
        setImapScanning(true);
        setImapResult(null);
        try {
            const r = await axios.post(`${API_URL}/api/ovh/scan-imap`, {}, { withCredentials: true });
            setImapResult(r.data);
            if (r.data.error) toast.error(r.data.error);
            else toast.success(`${r.data.emails_processed || 0} email(s) traité(s), ${r.data.cdrs_inserted || 0} appel(s) importé(s)`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur scan IMAP');
        } finally {
            setImapScanning(false);
        }
    };

    const handleApiSync = async (wait = false) => {
        setApiSyncing(true);
        setApiSyncResult(null);
        try {
            const url = `${API_URL}/api/ovh/sync-now${wait ? '?wait=1' : ''}`;
            const r = await axios.post(url, {}, { withCredentials: true, timeout: wait ? 60000 : 10000 });
            setApiSyncResult(r.data);
            if (r.data.started) {
                toast.success("Synchronisation lancée — les badges se mettront à jour dans 1-2 min.");
            } else if (r.data.error) {
                toast.error(r.data.error);
            } else {
                toast.success(`${r.data.inserted || 0} nouveaux appels importés`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur synchro OVH');
        } finally {
            setApiSyncing(false);
        }
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-6 p-4 sm:p-6">
                <div className="flex items-center gap-3">
                    <Download className="w-6 h-6 text-[#C9A962]" />
                    <h1 className="text-2xl font-bold text-[#1B365D]">Import appels OVH</h1>
                </div>

                {/* Info box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-testid="info-box">
                    <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-900 space-y-2">
                        <p className="font-semibold">3 sources d'import automatiques + manuelles</p>
                        <p>
                            <strong>1. API OVH (temps réel)</strong> — les appels du mois en cours sont récupérés toutes les 10 min via l'API
                            <code className="bg-blue-100 px-1 rounded">voiceConsumption</code> (entrants + sortants).
                            <br />
                            <strong>2. Scan IMAP</strong> — toutes les 6 h, le CRM lit la boîte mail à la recherche de CSV envoyés par OVH.
                            <br />
                            <strong>3. Import manuel</strong> — vous pouvez glisser un export CSV depuis le Manager OVH ci-dessous.
                        </p>
                    </div>
                </div>

                {/* API OVH live sync */}
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-testid="api-sync-card">
                    <h2 className="font-semibold text-[#1B365D] mb-2 flex items-center gap-2">
                        <Phone className="w-4 h-4" /> Synchronisation API OVH (temps réel)
                    </h2>
                    <p className="text-sm text-gray-600 mb-4">
                        Récupère immédiatement les appels du mois en cours (entrants + sortants) directement depuis l'API OVH avec votre clé d'application. La synchro tourne automatiquement toutes les 10 minutes en arrière-plan.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Button
                            disabled={apiSyncing}
                            onClick={() => handleApiSync(false)}
                            className="bg-[#1B365D] hover:bg-[#15294a] text-white gap-2"
                            data-testid="api-sync-btn"
                        >
                            {apiSyncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                            Synchroniser maintenant
                        </Button>
                    </div>
                    {apiSyncResult && (
                        <div className="mt-4 text-sm" data-testid="api-sync-result">
                            {apiSyncResult.started ? (
                                <div className="text-blue-700">
                                    Synchronisation en cours en arrière-plan — patientez 1 à 2 minutes puis rafraîchissez la liste des prospects pour voir les badges mis à jour.
                                </div>
                            ) : apiSyncResult.error ? (
                                <div className="text-red-600">{apiSyncResult.error}</div>
                            ) : (
                                <div className="text-green-700">
                                    {apiSyncResult.inserted || 0} nouveaux appels · {apiSyncResult.updated || 0} mis à jour · {apiSyncResult.total || 0} traités
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Manual upload */}
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-testid="manual-upload-card">
                    <h2 className="font-semibold text-[#1B365D] mb-4 flex items-center gap-2">
                        <Upload className="w-4 h-4" /> Import manuel
                    </h2>
                    <div
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={onDrop}
                        onClick={() => inputRef.current?.click()}
                        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition ${
                            dragOver ? 'border-[#C9A962] bg-amber-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                        }`}
                        data-testid="dropzone"
                    >
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".csv,.txt,text/csv"
                            className="hidden"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            data-testid="file-input"
                        />
                        <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                        {file ? (
                            <>
                                <p className="text-sm font-medium text-[#1B365D]">{file.name}</p>
                                <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} Ko</p>
                            </>
                        ) : (
                            <>
                                <p className="text-sm text-gray-600">Glissez un fichier CSV ici ou cliquez pour sélectionner</p>
                                <p className="text-xs text-gray-400 mt-1">Formats : .csv exportés depuis OVH Manager</p>
                            </>
                        )}
                    </div>
                    <div className="flex items-center justify-end gap-3 mt-4">
                        {file && (
                            <Button variant="outline" size="sm" onClick={() => { setFile(null); setResult(null); }} data-testid="clear-file">
                                Changer
                            </Button>
                        )}
                        <Button
                            disabled={!file || uploading}
                            onClick={handleUpload}
                            className="bg-[#C9A962] hover:bg-[#B8984F] text-white gap-2"
                            data-testid="upload-btn"
                        >
                            {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                            {uploading ? 'Import en cours...' : 'Importer le CSV'}
                        </Button>
                    </div>

                    {result && (
                        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center" data-testid="import-result">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="text-2xl font-bold text-green-600">{result.inserted}</div>
                                <div className="text-xs text-green-700">Nouveaux appels</div>
                            </div>
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                                <div className="text-2xl font-bold text-emerald-600">{result.incoming_new}</div>
                                <div className="text-xs text-emerald-700">Entrants</div>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="text-2xl font-bold text-blue-600">{result.outgoing_new}</div>
                                <div className="text-xs text-blue-700">Sortants</div>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <div className="text-2xl font-bold text-amber-600">{result.prospects_matched}</div>
                                <div className="text-xs text-amber-700">Prospects liés</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* IMAP automatic scan */}
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-testid="imap-card">
                    <h2 className="font-semibold text-[#1B365D] mb-4 flex items-center gap-2">
                        <Mail className="w-4 h-4" /> Scan automatique (email OVH)
                    </h2>
                    <p className="text-sm text-gray-600 mb-3">
                        Le CRM scanne automatiquement <code className="bg-gray-100 px-1 rounded">contact@luxymove.fr</code> toutes les 6 h pour importer les CSV
                        envoyés par OVH. Vous pouvez forcer un scan immédiat ci-dessous.
                    </p>
                    <Button
                        disabled={imapScanning}
                        onClick={handleScanImap}
                        variant="outline"
                        className="gap-2"
                        data-testid="imap-scan-btn"
                    >
                        {imapScanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                        {imapScanning ? 'Scan en cours...' : 'Scanner la boîte mail maintenant'}
                    </Button>
                    {imapResult && (
                        <div className="mt-3 text-sm" data-testid="imap-result">
                            {imapResult.error ? (
                                <div className="text-red-600">{imapResult.error}</div>
                            ) : (
                                <div className="text-green-700">
                                    ✅ {imapResult.emails_processed} email(s) traité(s), {imapResult.cdrs_inserted} nouvel appel(s) importé(s)
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
